import type { Position, GamepadLayout } from './store';

/**
 * Normalize a position string to ensure it has a % suffix
 * Fixes legacy data where positions were saved without % (e.g., "25" instead of "25%")
 * @param value - Position value (e.g., "25" or "25%")
 * @returns Normalized position with % suffix (e.g., "25%")
 */
export const normalizePositionValue = (value: string): string => {
  if (!value) return value;

  // Already has % suffix
  if (value.endsWith('%')) return value;

  // If it's a valid number, add % suffix
  if (!isNaN(parseFloat(value))) {
    return value + '%';
  }

  // Return as-is if not a number (shouldn't happen, but safe fallback)
  return value;
};

/**
 * Normalize a position object to ensure x/y values always have % suffix
 * @param position - Position object with x, y, scale properties
 * @returns Normalized position object
 */
export const normalizePosition = (position: Position): Position => {
  return {
    ...position,
    x: normalizePositionValue(position.x),
    y: normalizePositionValue(position.y),
  };
};

/**
 * Normalize a layout object to ensure all positions have % suffix
 * @param layout - Partial or full gamepad layout
 * @returns Normalized layout with all positions having % suffix
 */
export const normalizeLayout = (layout: Partial<GamepadLayout>): Partial<GamepadLayout> => {
  const normalized: Partial<GamepadLayout> = {};

  for (const [key, position] of Object.entries(layout)) {
    if (position && typeof position === 'object' && 'x' in position && 'y' in position) {
      normalized[key as keyof GamepadLayout] = normalizePosition(position);
    }
  }

  return normalized as Partial<GamepadLayout>;
};
